// src/hooks/useBusinessMetrics.ts
// Re-cableado a lealify-base (/analytics/metrics) — reemplaza Elasticsearch/Firestore.
// Conserva la interfaz BusinessMetrics y el shape de retorno que ya consumen
// BusinessSnapshotPanel y ProgramHealthPanel.

import { useState, useEffect, useCallback } from 'react';
import { analyticsApi } from '../api/analyticsApi';

// --- Interfaz BusinessMetrics (sin cambios de forma; se mantiene compatibilidad) ---
interface BusinessMetrics {
    totalVisits: number;
    totalPointsGiven: number;
    uniqueCustomers: number;
    averageVisitFrequency: number;
    lastStatsUpdate: Date | null;
    maleCustomers: number;
    femaleCustomers: number;
    unknownGenderCustomers: number;

    zeroVisitClients: number;
    oneVisitClients: number;
    multiVisitClients: number;

    retentionRate: number;
    oneTimeVisitors: number;
    returningClients: number;

    activeLastMonth: number;
    activeLast3Months: number;

    activeRewardsCount?: number;

    error?: string | null;
}

const EMPTY: BusinessMetrics = {
    totalVisits: 0, totalPointsGiven: 0, uniqueCustomers: 0, averageVisitFrequency: 0,
    lastStatsUpdate: null, maleCustomers: 0, femaleCustomers: 0, unknownGenderCustomers: 0,
    zeroVisitClients: 0, oneVisitClients: 0, multiVisitClients: 0, retentionRate: 0,
    oneTimeVisitors: 0, returningClients: 0, activeLastMonth: 0, activeLast3Months: 0,
    activeRewardsCount: 0, error: null,
};

// `businessId` acepta null porque así lo trae `currentUser.businessId` (dueño sin negocio
// creado todavía). El `if (!businessId)` de abajo ya lo cubre: deja métricas en cero.
export const useBusinessMetrics = (businessId?: string | null, _businessTimeZone: string = 'America/Mexico_City') => {
    const [metrics, setMetrics] = useState<BusinessMetrics>(EMPTY);
    const [branchMetrics] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating] = useState(false);

    const loadMetrics = useCallback(async () => {
        if (!businessId) {
            setMetrics(EMPTY);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const m = await analyticsApi.businessMetrics();
            setMetrics({
                totalVisits: m.totalVisits ?? 0,
                totalPointsGiven: m.totalPointsGiven ?? 0,
                uniqueCustomers: m.uniqueCustomers ?? 0,
                averageVisitFrequency: 0,
                lastStatsUpdate: m.lastUpdated ? new Date(m.lastUpdated) : new Date(),
                maleCustomers: m.maleCustomers ?? 0,
                femaleCustomers: m.femaleCustomers ?? 0,
                unknownGenderCustomers: m.unknownGenderCustomers ?? 0,
                zeroVisitClients: m.zeroVisitClients ?? 0,
                oneVisitClients: m.oneVisitClients ?? 0,
                multiVisitClients: m.multiVisitClients ?? 0,
                retentionRate: m.retentionRate ?? 0,
                oneTimeVisitors: m.oneTimeVisitors ?? 0,
                returningClients: m.returningClients ?? 0,
                activeLastMonth: 0,
                activeLast3Months: 0,
                activeRewardsCount: m.activeRewardsCount ?? 0,
                error: null,
            });
        } catch (e: any) {
            const msg = `Error al cargar métricas: ${e?.message ?? String(e)}`;
            console.error('(useBusinessMetrics)', msg);
            setError(msg);
            setMetrics({ ...EMPTY, error: msg });
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => { loadMetrics(); }, [businessId, loadMetrics]);

    return { metrics, branchMetrics, loading, error, updating };
};
